import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import {
  FaRegCopy,
  FaCheckCircle,
  FaUndo,
  FaSyncAlt,
  FaRegEye,
  FaTimes,
  FaShieldAlt,
  FaDatabase,
} from "react-icons/fa";
import { ThemeContext } from "../../contexts/themeContext";
import { DataGrid } from "../../common";
import "../../assets/styles/card-blacklist.scss";
import "../../assets/styles/audit-logs.scss";
import { APIEndpoint } from "../../utils/axios";

// Helper component for truncated text with copy to clipboard functionality
const CopyableTruncatedCell = ({ text, maxLength = 14, className = "" }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.stopPropagation();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  const displayText =
    text && text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text || "—";

  return (
    <div className={`copyable-truncated-cell ${className}`}>
      <span className="cell-text" title={text}>
        {displayText}
      </span>
      {text && (
        <button
          type="button"
          className={`copy-btn ${copied ? "copied" : ""}`}
          onClick={handleCopy}
          title={copied ? "Copied!" : "Copy to clipboard"}
        >
          {copied ? <FaCheckCircle size={11} /> : <FaRegCopy size={11} />}
        </button>
      )}
    </div>
  );
};

export default function AuditLogs() {
  const { t } = useTranslation();
  const { themedColor } = useContext(ThemeContext);

  // Active Tab State: 'sso' (SSO & Auth Logs) vs 'audit-trails' (System Audit Trails)
  const [activeTab, setActiveTab] = useState("sso");

  // ==========================================
  // TAB 1: SSO & AUTH LOGS STATE & LOGIC
  // ==========================================
  const [ssoLogs, setSsoLogs] = useState([]);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoError, setSsoError] = useState(null);
  const sessionEventFired = useRef(false);

  // Resolve current operator from localStorage credentials
  const resolveOperator = () => {
    try {
      const raw = localStorage.getItem("login_credentials");
      if (raw) {
        const parsed = JSON.parse(raw);
        return (
          parsed?.data?.userName ||
          parsed?.data?.email ||
          "superadmin@forthcode.com"
        );
      }
    } catch (_) {
      // ignore parse errors
    }
    return "superadmin@forthcode.com";
  };

  const fetchSsoLogs = async () => {
    try {
      setSsoLoading(true);
      setSsoError(null);
      let apiItems = [];
      try {
        const res = await APIEndpoint.get("idp/audit-logs");
        if (res?.items) {
          apiItems = res.items;
        } else if (res?.data?.items) {
          apiItems = res.data.items;
        }
      } catch (error) {
        console.error("Error fetching SSO audit logs:", error);
        setSsoError("Failed to load SSO audit logs. Please try again.");
      }

      const operator = resolveOperator();
      const sessionLoginEvent = {
        id: "session_login_" + Date.now(),
        event: "sso_login_success",
        cardNo: null,
        details: `Super Admin signed in via Forthcode SSO. Active session initialized.`,
        user: operator,
        timestamp: new Date().toISOString(),
        _inMemory: true,
      };

      setSsoLogs((prev) => {
        const alreadyHasSession = prev.some((l) => l._inMemory);
        if (alreadyHasSession) return apiItems;
        return [sessionLoginEvent, ...apiItems];
      });
    } catch (err) {
      console.error("Error fetching SSO audit logs:", err);
      setSsoError("Failed to load SSO audit logs. Please try again.");
    } finally {
      setSsoLoading(false);
    }
  };

  useEffect(() => {
    if (!sessionEventFired.current) {
      sessionEventFired.current = true;
      fetchSsoLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SSO Ag-Grid Renderers & Columns
  const SsoEventTypeRenderer = (params) => {
    if (!params.data) return null;
    const eventLabel = (params.data.event || "").replace(/_/g, " ");
    return (
      <span className={`badge-event ${params.data.event}`}>{eventLabel}</span>
    );
  };

  const SsoColumns = [
    {
      field: "event",
      headerName: "Event Type",
      flex: 1,
      minWidth: 130,
      cellRenderer: SsoEventTypeRenderer,
    },
    {
      field: "details",
      headerName: "Log Detail Message",
      flex: 2,
      minWidth: 400,
      cellStyle: {
        fontWeight: "500",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    },
    {
      field: "user",
      headerName: "Account",
      flex: 1.2,
      minWidth: 180,
      cellStyle: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    },
    {
      field: "timestamp",
      headerName: "Timestamp",
      flex: 1.2,
      minWidth: 170,
      valueFormatter: (params) =>
        params.value ? new Date(params.value).toLocaleString() : "",
      sort: "desc",
    },
  ];

  // ==========================================
  // TAB 2: SYSTEM AUDIT TRAILS STATE & LOGIC
  // ==========================================
  const DEFAULT_RECORD_TYPES = [
    "cashRegister",
    "categories",
    "items",
    "orderPayments",
    "recipes",
    "stores",
    "subcategories",
    "users",
    "warehouse",
  ];

  const [recordTypesList, setRecordTypesList] = useState(DEFAULT_RECORD_TYPES);

  const [userIdInput, setUserIdInput] = useState("");
  const [debouncedUserId, setDebouncedUserId] = useState("");

  const [eventIdInput, setEventIdInput] = useState("");
  const [debouncedEventId, setDebouncedEventId] = useState("");

  const [documentIdInput, setDocumentIdInput] = useState("");
  const [debouncedDocumentId, setDebouncedDocumentId] = useState("");

  const [recordType, setRecordType] = useState("");
  const [eventType, setEventType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [resetDataSource, setResetDataSource] = useState(false);
  const tableRef = useRef();

  const [trailError, setTrailError] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [copiedModalJson, setCopiedModalJson] = useState(false);

  // Helper to convert camelCase to Title Case (e.g. orderPayments -> Order Payments)
  const formatRecordTypeLabel = (str) => {
    if (!str) return "";
    const text = str.replace(/([A-Z])/g, " $1");
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  // Helper to format diff field values (including ISO dates) nicely
  const formatDiffValue = (val) => {
    if (val === null || val === undefined || val === "") return "—";
    if (typeof val === "object") return JSON.stringify(val, null, 2);
    if (
      typeof val === "string" &&
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(val)
    ) {
      const d = new Date(val);
      if (!isNaN(d.getTime())) {
        return `${d.toLocaleString()} (${val})`;
      }
    }
    return String(val);
  };

  // Apply 300ms Debounce to User ID Search Input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedUserId(userIdInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [userIdInput]);

  // Apply 300ms Debounce to Event ID Search Input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEventId(eventIdInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [eventIdInput]);

  // Apply 300ms Debounce to Document ID Search Input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedDocumentId(documentIdInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [documentIdInput]);

  // Trigger DataGrid reload when filters change
  useEffect(() => {
    if (activeTab === "audit-trails") {
      setResetDataSource((prev) => !prev);
    }
  }, [
    debouncedUserId,
    debouncedEventId,
    recordType,
    eventType,
    startDate,
    endDate,
    debouncedDocumentId,
    activeTab,
  ]);

  // Server-side DataGrid getRows callback
  const fetchAuditTrails = useCallback(
    async (gridPage, gridLimit) => {
      try {
        setTrailError(null);

        // DataGrid calculates gridPage as (endRow / 50). Map to 1-based API page number
        const pageLimit = gridLimit || 50;
        const rawGridPage = gridPage || 1;
        const apiPage = Math.max(
          1,
          Math.round((rawGridPage * 50 - pageLimit) / pageLimit) + 1,
        );

        const queryParams = new URLSearchParams();
        if (debouncedUserId.trim()) {
          queryParams.append("userId", debouncedUserId.trim());
          queryParams.append("user", debouncedUserId.trim());
        }
        if (recordType) {
          queryParams.append("recordType", recordType);
        }
        if (eventType) {
          queryParams.append("eventType", eventType);
        }
        if (debouncedEventId.trim()) {
          queryParams.append("eventId", debouncedEventId.trim());
        }
        if (startDate) {
          queryParams.append("startDate", startDate);
        }
        if (endDate) {
          queryParams.append("endDate", endDate);
        }
        if (debouncedDocumentId.trim()) {
          queryParams.append("documentId", debouncedDocumentId.trim());
          queryParams.append("recordId", debouncedDocumentId.trim());
        }
        queryParams.append("page", apiPage);
        queryParams.append("limit", pageLimit);

        const path = `audit-trails?${queryParams.toString()}`;
        const res = await APIEndpoint.get(path);

        const responseData = res?.items
          ? res
          : res?.data?.items
            ? res.data
            : res?.data || res;
        const items =
          responseData?.items ||
          (Array.isArray(responseData) ? responseData : []);
        const count = responseData?.count ?? items.length;

        if (
          responseData?.recordTypes &&
          Array.isArray(responseData.recordTypes) &&
          responseData.recordTypes.length > 0
        ) {
          setRecordTypesList(responseData.recordTypes);
        }

        return {
          items,
          count,
        };
      } catch (err) {
        console.error("Failed to fetch audit trails:", err);
        setTrailError(
          "Failed to load system audit trails. Please check connection or try again.",
        );
        return { items: [], count: 0 };
      }
    },
    [
      debouncedUserId,
      debouncedEventId,
      recordType,
      eventType,
      startDate,
      endDate,
      debouncedDocumentId,
    ],
  );

  const handleResetTrailFilters = () => {
    setUserIdInput("");
    setDebouncedUserId("");
    setEventIdInput("");
    setDebouncedEventId("");
    setDocumentIdInput("");
    setDebouncedDocumentId("");
    setRecordType("");
    setEventType("");
    setStartDate("");
    setEndDate("");
    setResetDataSource((prev) => !prev);
  };

  const handleOpenDetailModal = (logItem) => {
    setSelectedLog(logItem);
    setIsDetailModalOpen(true);
    setCopiedModalJson(false);
  };

  const handleCopyModalJson = async () => {
    if (!selectedLog) return;
    try {
      const payload = selectedLog;
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopiedModalJson(true);
      setTimeout(() => setCopiedModalJson(false), 1500);
    } catch (err) {
      console.error("Failed to copy JSON:", err);
    }
  };

  // Operation Type Badge Renderer
  const OperationTypeRenderer = (params) => {
    if (!params.data) return null;
    const op = (
      params.data.eventType ||
      params.data.operationType ||
      params.data.event ||
      ""
    ).toLowerCase();

    let opClass = "op-default";
    if (op === "insert" || op === "create") opClass = "op-insert";
    else if (op === "update" || op === "modify") opClass = "op-update";
    else if (op === "delete" || op === "remove") opClass = "op-delete";

    return (
      <span className={`badge-op ${opClass}`}>{op.toUpperCase() || "N/A"}</span>
    );
  };

  // Collection Chip Renderer
  const CollectionRenderer = (params) => {
    if (!params.data) return null;
    const rawType = params.data.recordType || params.data.collectionName || "—";
    return <span>{formatRecordTypeLabel(rawType)}</span>;
  };

  // Action Cell Renderer
  const ActionCellRenderer = (params) => {
    if (!params.data) return null;
    return (
      <div className="action-btn-cell">
        <button
          type="button"
          onClick={() => handleOpenDetailModal(params.data)}
          title="View full payload and diff details"
        >
          <FaRegEye size={16} /> Details
        </button>
      </div>
    );
  };

  // Audit Trails Ag-Grid Columns Definition
  const TrailColumns = [
    {
      field: "createdOn",
      headerName: "Timestamp",
      flex: 1.2,
      minWidth: 170,
      valueGetter: (params) => params.data?.createdOn || params.data?.timestamp,
      valueFormatter: (params) =>
        params.value ? new Date(params.value).toLocaleString() : "—",
      sort: "desc",
    },
    {
      field: "eventId",
      headerName: "Event ID",
      flex: 1.3,
      minWidth: 160,
      cellRenderer: (params) => (
        <CopyableTruncatedCell
          text={params.data?.eventId || "—"}
          maxLength={14}
        />
      ),
    },
    {
      field: "recordType",
      headerName: "Entity Collection",
      flex: 1.2,
      minWidth: 150,
      cellRenderer: CollectionRenderer,
    },
    {
      field: "eventType",
      headerName: "Operation",
      flex: 1,
      minWidth: 110,
      cellRenderer: OperationTypeRenderer,
    },
    {
      field: "userId",
      headerName: "User / Account",
      flex: 1.2,
      minWidth: 150,
      cellRenderer: (params) => (
        <CopyableTruncatedCell
          text={params.data?.userId || params.data?.user || "System"}
          maxLength={18}
        />
      ),
    },
    {
      headerName: "Action",
      flex: 1,
      minWidth: 50,
      cellRenderer: ActionCellRenderer,
    },
  ];

  const isDark = themedColor?.sidebar === "#181818";

  return (
    <div className={`audit-logs-container ${isDark ? "dark" : "light"}`}>
      {/* Header Row */}
      <div className="audit-header-row">
        <div className="title-area">
          <h1>{t("pages.controlPanelMenuSub.auditLogs") || "Audit Logs"}</h1>
          <p>
            {activeTab === "sso"
              ? "Security logging and session activity audit trail — sourced from server-side SSO changeset log"
              : "Real-time document changes, user activities, and field diffs across domain entities"}
          </p>
        </div>
        <div className="audit-header-actions">
          <button
            type="button"
            className="btn-premium btn-secondary"
            onClick={
              activeTab === "sso"
                ? fetchSsoLogs
                : () => setResetDataSource((prev) => !prev)
            }
            disabled={ssoLoading}
            title="Refresh audit log dataset"
          >
            <FaSyncAlt className={ssoLoading ? "spin" : ""} size={12} />
            {ssoLoading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Tabs Navigation Bar */}
      <div className="audit-tabs-bar">
        <button
          type="button"
          className={`audit-tab-btn ${activeTab === "sso" ? "active" : ""}`}
          onClick={() => setActiveTab("sso")}
        >
          <FaShieldAlt size={14} /> SSO & Auth Logs
        </button>

        <button
          type="button"
          className={`audit-tab-btn ${activeTab === "audit-trails" ? "active" : ""}`}
          onClick={() => setActiveTab("audit-trails")}
        >
          <FaDatabase size={14} /> System Audit Trails
        </button>
      </div>

      {/* ========================================== */}
      {/* TAB 1: SSO & AUTH LOGS VIEW                */}
      {/* ========================================== */}
      {activeTab === "sso" && (
        <>
          {ssoError && (
            <div
              style={{
                padding: "12px 16px",
                borderRadius: "8px",
                background: "rgba(220,53,69,0.12)",
                border: "1px solid rgba(220,53,69,0.3)",
                color: "#ff6b6b",
                fontSize: "13px",
              }}
            >
              {ssoError}
            </div>
          )}

          <div className="flexCol full-flex ag-theme-alpine-dark">
            <DataGrid
              className="full-flex"
              columns={SsoColumns}
              showDefaultFilters={true}
              enableGridSearch={true}
              rowModelType="clientSide"
              rowData={ssoLogs}
              columnResize={true}
              pagination={true}
              paginationPageSize={15}
              suppressPaginationPanel={false}
            />
          </div>
        </>
      )}

      {activeTab === "audit-trails" && (
        <>
          {/* Filter Controls Bar */}
          <div className="audit-filters-card">
            <div className="filters-row">
              {/* User Search (Debounced 300ms) */}
              <div className="filter-group">
                <label>User Email / ID</label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    placeholder="Search User..."
                    value={userIdInput}
                    onChange={(e) => setUserIdInput(e.target.value)}
                  />
                </div>
              </div>

              {/* Record Type Dropdown */}
              <div className="filter-group">
                <label>Entity Collection</label>
                <div className="input-wrapper">
                  <select
                    value={recordType}
                    onChange={(e) => setRecordType(e.target.value)}
                  >
                    <option value="">All Collections</option>
                    {recordTypesList.map((type) => (
                      <option key={type} value={type}>
                        {formatRecordTypeLabel(type)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Event Type Dropdown */}
              <div className="filter-group">
                <label>Operation Type</label>
                <div className="input-wrapper">
                  <select
                    value={eventType}
                    onChange={(e) => setEventType(e.target.value)}
                  >
                    <option value="">All Operations</option>
                    <option value="insert">Insert (Created)</option>
                    <option value="update">Update (Modified)</option>
                    <option value="delete">Delete (Removed)</option>
                  </select>
                </div>
              </div>

              {/* Event ID (Debounced 300ms) */}
              <div className="filter-group">
                <label>Event ID</label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    placeholder="Search Event ID..."
                    value={eventIdInput}
                    onChange={(e) => setEventIdInput(e.target.value)}
                  />
                </div>
              </div>

              {/* Document ID (Debounced 300ms) */}
              <div className="filter-group">
                <label>Document ID</label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    placeholder="Search Doc ID..."
                    value={documentIdInput}
                    onChange={(e) => setDocumentIdInput(e.target.value)}
                  />
                </div>
              </div>

              {/* Start Date */}
              <div className="filter-group">
                <label>Start Date</label>
                <div className="input-wrapper">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
              </div>

              {/* End Date */}
              <div className="filter-group">
                <label>End Date</label>
                <div className="input-wrapper">
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>

              {/* Filter Reset Action */}
              <div className="filter-actions">
                <button
                  type="button"
                  className="btn-premium btn-secondary"
                  onClick={handleResetTrailFilters}
                  title="Reset all filters"
                >
                  <FaUndo size={11} /> Reset
                </button>
              </div>
            </div>
          </div>

          {/* Error Notice */}
          {trailError && (
            <div
              style={{
                padding: "12px 16px",
                borderRadius: "8px",
                background: "rgba(220,53,69,0.12)",
                border: "1px solid rgba(220,53,69,0.3)",
                color: "#ff6b6b",
                fontSize: "13px",
              }}
            >
              {trailError}
            </div>
          )}

          {/* Data Table with DataGrid Default Server-Side Pagination */}
          <div className="audit-grid-container flexCol full-flex ag-theme-alpine-dark">
            <DataGrid
              ref={tableRef}
              className="full-flex"
              columns={TrailColumns}
              initialPageLimit={50}
              showDefaultFilters={false}
              enableGridSearch={false}
              getRows={fetchAuditTrails}
              resetDataSource={resetDataSource}
              columnResize={true}
            />
          </div>
        </>
      )}

      {/* Detail / Diff Modal */}
      {isDetailModalOpen && selectedLog && (
        <div
          className="modal-overlay"
          onClick={() => setIsDetailModalOpen(false)}
        >
          <div
            className="audit-detail-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div className="modal-title">
                <h3>Audit Trail Event Details</h3>
                <span
                  className={`badge-op op-${(selectedLog.eventType || selectedLog.operationType || "").toLowerCase()}`}
                >
                  {(
                    selectedLog.eventType ||
                    selectedLog.operationType ||
                    "EVENT"
                  ).toUpperCase()}
                </span>
                <span className="badge-collection">
                  {formatRecordTypeLabel(
                    selectedLog.recordType || selectedLog.collectionName,
                  ) || "Entity"}
                </span>
              </div>
              <button
                type="button"
                className="close-btn"
                onClick={() => setIsDetailModalOpen(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              {/* Event Metadata Grid */}
              <div className="meta-grid">
                <div className="meta-item">
                  <span className="meta-label">Event ID</span>
                  <span className="meta-value">
                    {selectedLog.eventId || selectedLog._id || "—"}
                  </span>
                </div>
                {selectedLog.dbName && (
                  <div className="meta-item">
                    <span className="meta-label">Database Name</span>
                    <span className="meta-value">{selectedLog.dbName}</span>
                  </div>
                )}
                <div className="meta-item">
                  <span className="meta-label">Target Collection</span>
                  <span className="meta-value">
                    {formatRecordTypeLabel(
                      selectedLog.recordType || selectedLog.collectionName,
                    ) || "—"}
                  </span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Operation Type</span>
                  <span className="meta-value">
                    {(
                      selectedLog.eventType ||
                      selectedLog.operationType ||
                      "—"
                    ).toUpperCase()}
                  </span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Event Timestamp</span>
                  <span className="meta-value">
                    {selectedLog.createdOn || selectedLog.timestamp
                      ? new Date(
                          selectedLog.createdOn || selectedLog.timestamp,
                        ).toLocaleString()
                      : "—"}
                  </span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">User / Account</span>
                  <span className="meta-value">
                    {selectedLog.userId || selectedLog.user || "System"}
                  </span>
                </div>
              </div>

              {/* Modified Field Diffs (Changelist Array) */}
              {Array.isArray(selectedLog.changelist) &&
                selectedLog.changelist.length > 0 && (
                  <>
                    <div className="section-title">
                      <span>
                        Field Changes ({selectedLog.changelist.length})
                      </span>
                    </div>
                    <table className="diff-table">
                      <thead>
                        <tr>
                          <th>Field Name</th>
                          <th>Old Value</th>
                          <th>New Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedLog.changelist.map((change, idx) => (
                          <tr key={idx}>
                            <td className="field-key">{change.field}</td>
                            <td className="field-value old-val">
                              {formatDiffValue(change.oldValue)}
                            </td>
                            <td className="field-value new-val">
                              {formatDiffValue(change.newValue)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

              {/* Updated Fields Diff Table (Fallback for legacy format) */}
              {(!Array.isArray(selectedLog.changelist) ||
                selectedLog.changelist.length === 0) &&
                selectedLog.updatedFields &&
                Object.keys(selectedLog.updatedFields).length > 0 && (
                  <>
                    <div className="section-title">
                      <span>Modified Field Diffs</span>
                    </div>
                    <table className="diff-table">
                      <thead>
                        <tr>
                          <th>Field Name</th>
                          <th>New Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(selectedLog.updatedFields).map(
                          ([key, val]) => (
                            <tr key={key}>
                              <td className="field-key">{key}</td>
                              <td className="field-value new-val">
                                {typeof val === "object"
                                  ? JSON.stringify(val)
                                  : String(val)}
                              </td>
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  </>
                )}

              {/* Full Document Snapshot (For Inserts or Full Snapshots) */}
              {selectedLog.fullDocument && (
                <>
                  <div className="section-title">
                    <span>Full Document Snapshot</span>
                  </div>
                  <div className="json-code-box">
                    {JSON.stringify(selectedLog.fullDocument, null, 2)}
                  </div>
                </>
              )}

              {/* Complete Event JSON Box */}
              <div className="section-title">
                <span>Complete Audit Record JSON</span>
                <button
                  type="button"
                  className="btn-premium btn-secondary"
                  style={{ padding: "3px 8px", fontSize: "11px" }}
                  onClick={handleCopyModalJson}
                >
                  {copiedModalJson ? (
                    <>
                      <FaCheckCircle size={10} color="#28a745" /> Copied!
                    </>
                  ) : (
                    <>
                      <FaRegCopy size={10} /> Copy Payload JSON
                    </>
                  )}
                </button>
              </div>
              <div className="json-code-box">
                {JSON.stringify(selectedLog, null, 2)}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-premium btn-secondary"
                onClick={() => setIsDetailModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
